import type { CustomFile } from '../types';
import type { TypeUploadConfig } from '../send/types';

import type { TypeUploadRef, TypeItemBuild, TypeSendUploadItemConfig } from './types';
export type { TypeUploadRef, TypeItemBuild, TypeFnSendFile, TypeSendUploadItemConfig } from './types';

import { sendFiles } from '../send';
import exCreateUploadItem from '../uploadItem/create';
import * as exUploadItem from '../uploadItem';
import formatFileSummary from '../uploadItem/utils/formatFileSummary';

// ----------------------------------------------------------------------------

function send(element: HTMLElement, files: CustomFile[], config: TypeSendUploadItemConfig): TypeUploadRef[] {
    const itemsData = exUploadItem.prepareItemsData(files);

    const createFn = config.fnCreate ?? exCreateUploadItem;

    // Pass 1: create all UI items and per-item send configs (no HTTP requests yet)
    const builds: TypeItemBuild[] = itemsData.map((itemData) => {
        const uploadState = { isCompleted: false, percent: 0 };
        const controllerRef: { current: { getState: () => string } | null } = { current: null };

        const uploadItem = createFn({
            merge: config.uploadItem,
            title: itemData.title,
            subtitle: formatFileSummary(itemData.files.length, itemData.totalSize),
            uploadIcon: {
                attr: { title: itemData.isFolder ? 'Folder' : 'File' },
                type: itemData.isFolder ? 'fa fa-folder' : 'fa fa-file',
            },
            uploadProgressStacked: {
                data: [
                    { attr: { id: 'progress-success' }, striped: true, animated: true },
                    { attr: { id: 'progress-danger' }, progressBar: { class: { add: 'bg-danger' } } },
                ],
            },
            uploadRight: {},
        });
        element.appendChild(uploadItem.element);

        const elProgressStacked = uploadItem.elements.uploadItemProgressStacked;
        const elProgressSuccess = elProgressStacked?.querySelector<HTMLElement>('#progress-success');
        const elProgressDanger = elProgressStacked?.querySelector<HTMLElement>('#progress-danger');

        if (elProgressSuccess) {
            elProgressSuccess.style.width = '0%';
        }
        if (elProgressDanger) {
            elProgressDanger.style.width = '0%';
        }

        const fnSetProgress = (successPercent: number, errorPercent: number): void => {
            if (elProgressSuccess) {
                elProgressSuccess.style.width = `${successPercent}%`;
            }
            if (elProgressDanger) {
                elProgressDanger.style.width = `${errorPercent}%`;
            }
        };

        const sendConfig: TypeUploadConfig = {
            merge: config.uploadSend,
            url: config.url,
            method: config.method,
            fnProgressUpload: function (evt, param): void {
                if (uploadState.isCompleted) {
                    return;
                }

                const totalFiles = Number(param.totalFiles);
                const successCount = Number(param.successCount);
                const errorCount = Number(param.errorCount);
                const skippedCount = Number(param.skippedCount ?? 0);

                const successPercent = totalFiles > 0 ? (successCount / totalFiles) * 100 : 0;
                const errorPercent = totalFiles > 0 ? (errorCount / totalFiles) * 100 : 0;
                fnSetProgress(successPercent, errorPercent);

                // Cap at 99% while in-flight — allow 100% only once every file is server-confirmed.
                // Filtered-out files count as processed, otherwise the cap would never lift.
                const allProcessed = totalFiles > 0 && successCount + errorCount + skippedCount === totalFiles;
                const rawPercent = Math.max(0, Number(param.totalPercent ?? 0));
                const percent = Math.round(allProcessed ? rawPercent : Math.min(99, rawPercent));
                if (uploadState.percent !== percent) {
                    uploadState.percent = percent;
                    uploadItem.setPercentText(`${percent}%`);
                }

                if (controllerRef.current?.getState() !== 'paused') {
                    uploadItem.setStatus(totalFiles > 0 ? 'Uploading' : 'Ready');
                }
                config.fnProgressUpload?.(evt, param);
            },
            /*
             * Retries are otherwise invisible: the request has already failed, so no progress
             * event arrives during the backoff and the item would sit silently at its last
             * percent. The next attempt's first progress event restores "Uploading".
             */
            fnFileRetry: function (file, attempt, err): void {
                if (!uploadState.isCompleted) {
                    const maxRetries = config.uploadSend?.maxRetries ?? 0;

                    uploadItem.setStatus(
                        maxRetries > 0 ? `Retrying ${attempt}/${maxRetries}` : `Retrying (${attempt})`,
                    );
                }

                config.fnFileRetry?.(file, attempt, err);
            },
            fnSuccess: (result) => config.fnSuccess?.(result),
            fnFail: (result) => config.fnFail?.(result),
        };

        return { itemData, uploadItem, uploadState, controllerRef, sendConfig };
    });

    // Pass 2: start uploads
    const uploadResults = config.fnSendFile
        ? config.fnSendFile(builds, config)
        : builds.map((b) => sendFiles(config.mode, b.itemData.files, b.sendConfig));

    // Pass 3: build uploadRefs and attach completion handlers
    return builds.map((build, index) => {
        const uploadResult = uploadResults[index];
        const { uploadItem, uploadState, itemData, controllerRef } = build;
        controllerRef.current = uploadResult.controller;

        const uploadRef: TypeUploadRef = {
            element: uploadItem.element,
            elements: uploadItem.elements,
            promise: uploadResult.promise,
            controller: uploadResult.controller,
            state: uploadState,
            setStatus: (status: string): void => {
                uploadItem.setStatus(status);
            },
        };

        if (typeof config.fnStart === 'function') {
            config.fnStart(itemData.files, uploadRef);
        }

        uploadResult.promise
            .then((result): void => {
                uploadState.isCompleted = true;

                if (result.aborted) {
                    uploadItem.uploadAbort('Canceled');
                    return;
                }

                config.fnFinish?.(result, uploadRef);

                const successCount = result.success.length;
                const errorCount = result.error.length;

                if (errorCount === 0) {
                    uploadItem.setPercentText('100%');
                    uploadItem.setStatus('Completed');
                    config.fnComplete?.(result);
                } else if (successCount > 0) {
                    uploadItem.setPercentText('100%');
                    uploadItem.setStatus('Completed with errors');
                    config.fnError?.(result);
                } else {
                    uploadItem.setPercentText('100%');
                    uploadItem.uploadAbort('Error');
                    config.fnError?.(result);
                }
            })
            .catch((err): void => {
                uploadState.isCompleted = true;
                uploadItem.uploadAbort('Error');
                config.fnError?.(err);
                console.error('Unexpected error:', err);
            });

        return uploadRef;
    });
}

// ----------------------------------------------------------------------------

export default send;
